'use client'

import React, { useState } from 'react'
import Script from 'next/script'
import { Loader2 } from 'lucide-react'
import posthog from 'posthog-js'
import { trackContactFormSubmitted } from '../../lib/analytics'

/** Matches the prompts in the events brief so the inbound lead arrives pre-qualified. */
export const EVENT_INQUIRY_OPTIONS = [
  { value: 'booking', label: 'Booking Serotte Law for an event' },
  { value: 'attendance', label: 'Having our team attend your event' },
  { value: 'working-together', label: 'Learning more about working with us' },
] as const

const INITIAL_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  organization: '',
  inquiryType: EVENT_INQUIRY_OPTIONS[0].value as string,
  message: '',
}

const inputClasses =
  'w-full bg-white px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all'
const labelClasses = 'block text-sm font-medium text-gray-700 mb-1.5'

export function EventsContactForm() {
  const [formData, setFormData] = useState(INITIAL_FORM)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Honeypot: bots fill hidden fields, so fake a success and skip submitting
    const form = new FormData(e.currentTarget as HTMLFormElement)
    if (form.get('website') || form.get('fax')) {
      setSubmitted(true)
      return
    }

    setIsSubmitting(true)

    try {
      const token = await new Promise<string>((resolve, reject) => {
        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!, { action: 'submit_contact' })
            .then(resolve)
            .catch(reject)
        })
      })

      const inquiryLabel =
        EVENT_INQUIRY_OPTIONS.find((option) => option.value === formData.inquiryType)?.label ||
        formData.inquiryType

      const res = await fetch('/api/submit-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          formData: {
            ...formData,
            inquiryLabel,
            // Existing Zapier automation keys off these fields, so an events lead
            // travels the same route as a /contact submission.
            source: 'events_page',
            referralSource: 'Events Page',
            referralType: 'No Referral',
          },
        }),
      })

      const data = await res.json()

      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Submission failed')
      }

      setSubmitted(true)

      posthog.identify(formData.email, {
        name: `${formData.firstName} ${formData.lastName}`,
        phone: formData.phone,
        source: 'events_form',
      })
      trackContactFormSubmitted({ inquiry_type: `events_${formData.inquiryType}` })
    } catch (err) {
      console.error('Error submitting events form:', err)
      setError('Something went wrong. Please try again, or email info@serottelaw.com.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section id="events-contact" className="bg-brand-blue px-6 py-16 md:px-12 md:py-24">
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}`}
        strategy="afterInteractive"
      />

      <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-2 lg:gap-16">
        <div className="text-white">
          <h2 className="mb-4 text-3xl font-serif md:text-4xl">
            Let&apos;s host something{' '}
            <span className="italic text-brand-gold-light">together.</span>
          </h2>
          <p className="mb-6 text-white/80">
            Whether you&apos;d like us to speak at your event, host a session for your team, or
            simply talk through what working with us looks like — tell us what you have in mind and
            our team will follow up.
          </p>
          <ul className="space-y-3 text-white/70">
            {EVENT_INQUIRY_OPTIONS.map((option) => (
              <li key={option.value} className="flex items-start gap-3">
                <span
                  className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-gold"
                  aria-hidden="true"
                />
                {option.label}
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl bg-background p-6 md:p-8">
          {submitted ? (
            <div className="rounded-xl border border-green-200 bg-green-50 p-8 text-center">
              <h3 className="mb-2 text-xl font-medium text-gray-900">Message sent</h3>
              <p className="mb-6 text-gray-600">
                Thank you for reaching out. Our team will be in touch shortly.
              </p>
              <button
                type="button"
                onClick={() => {
                  setSubmitted(false)
                  setFormData(INITIAL_FORM)
                }}
                className="font-medium text-brand-blue hover:underline"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="space-y-5"
              aria-describedby="events-form-intro"
            >
              <p id="events-form-intro" className="text-sm text-gray-600">
                Fields marked with an asterisk are required.
              </p>

              {/* Honeypot fields - hidden from users */}
              <div aria-hidden="true" className="absolute -left-[9999px]">
                <input type="text" name="website" tabIndex={-1} autoComplete="off" />
                <input type="text" name="fax" tabIndex={-1} autoComplete="off" />
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="events-firstName" className={labelClasses}>
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="events-firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    autoComplete="given-name"
                    className={inputClasses}
                    placeholder="John"
                  />
                </div>
                <div>
                  <label htmlFor="events-lastName" className={labelClasses}>
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="events-lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    autoComplete="family-name"
                    className={inputClasses}
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="events-email" className={labelClasses}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="events-email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    className={inputClasses}
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="events-phone" className={labelClasses}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="events-phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    autoComplete="tel"
                    className={inputClasses}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="events-organization" className={labelClasses}>
                  Company or Organization
                </label>
                <input
                  type="text"
                  id="events-organization"
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  autoComplete="organization"
                  className={inputClasses}
                  placeholder="Acme Inc."
                />
              </div>

              <div>
                <label htmlFor="events-inquiryType" className={labelClasses}>
                  What can we help with? *
                </label>
                <select
                  id="events-inquiryType"
                  name="inquiryType"
                  value={formData.inquiryType}
                  onChange={handleChange}
                  required
                  className={inputClasses}
                >
                  {EVENT_INQUIRY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="events-message" className={labelClasses}>
                  Tell us about your event *
                </label>
                <textarea
                  id="events-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className={inputClasses}
                  placeholder="Dates you have in mind, audience, location, and anything else that would help."
                />
              </div>

              {error && (
                <p role="alert" className="text-sm font-medium text-red-600">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-blue px-8 py-3 font-medium text-white transition-colors hover:bg-brand-blue/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-gold disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                {isSubmitting ? 'Sending...' : 'Send message'}
              </button>

              <p className="text-xs text-gray-500">
                This site is protected by reCAPTCHA and the Google{' '}
                <a
                  href="https://policies.google.com/privacy"
                  className="underline hover:text-gray-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a
                  href="https://policies.google.com/terms"
                  className="underline hover:text-gray-700"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </a>{' '}
                apply.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  )
}
